/**
 * State machine ядра редактора записи (задача 77 §1.1, задача 89 §4.2).
 *
 * Шаги `вид → проект → элемент → часы`. Элемент сборки (§4.2) зависит от
 * проекта: при известном проекте (`projectId`) — шаг 3 (адресный список), при
 * неизвестном (ручной код) / пропущенном проекте элемент неприменим —
 * переход `проект → часы` минует шаг 3 (и `goBack` из часов возвращает к
 * проекту, а не к пустому шагу элемента).
 *
 * Грабли визарда, которые обязаны выжить:
 *   - `projectSkipped` / `elementSkipped` — «осознанно пропустил» ≠ «не выбрано»;
 *   - `goBack` (× на шагах 2+ в create = назад без очистки) vs `resetFromStep`
 *     (pill-Trash2 = сброс шага и всего после).
 */
import { EMPTY_DRAFT, type Action, type State, type Step } from "./types";

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "selectWorkType": {
      return {
        step: 2,
        draft: {
          ...EMPTY_DRAFT,
          workTypeId: action.id,
        },
      };
    }
    case "selectWorkTypeText": {
      return {
        step: 2,
        draft: {
          ...EMPTY_DRAFT,
          workTypeText: action.text,
        },
      };
    }
    case "selectProjectId": {
      // Проект известен → шаг «элемент сборки». Смена проекта сбрасывает
      // элемент и часы (адресный список элементов зависит от проекта).
      return {
        step: 3,
        draft: {
          ...state.draft,
          projectId: action.projectId,
          projectCodeRaw: null,
          projectSkipped: false,
          assemblyElementId: null,
          elementSkipped: false,
          hoursHalf: null,
          // §115/2.1 — идентификатор доработки привязан к паре; смена проекта
          // меняет пару → сбрасываем.
          reworkNote: null,
        },
      };
    }
    case "selectProjectRaw": {
      // Проект неизвестен (ручной код) → элемента для него нет, минуем шаг 3.
      return {
        step: 4,
        draft: {
          ...state.draft,
          projectId: null,
          projectCodeRaw: action.code,
          projectSkipped: false,
          assemblyElementId: null,
          elementSkipped: false,
          hoursHalf: null,
          reworkNote: null,
        },
      };
    }
    case "skipProject": {
      // Без проекта → элемента нет, минуем шаг 3.
      return {
        step: 4,
        draft: {
          ...state.draft,
          projectId: null,
          projectCodeRaw: null,
          projectSkipped: true,
          assemblyElementId: null,
          elementSkipped: false,
          hoursHalf: null,
          reworkNote: null,
        },
      };
    }
    case "selectElement": {
      return {
        step: 4,
        draft: {
          ...state.draft,
          assemblyElementId: action.id,
          elementSkipped: false,
        },
      };
    }
    case "skipElement": {
      return {
        step: 4,
        draft: {
          ...state.draft,
          assemblyElementId: null,
          elementSkipped: true,
        },
      };
    }
    case "setHours": {
      return { ...state, draft: { ...state.draft, hoursHalf: action.half } };
    }
    case "setReworkNote": {
      return { ...state, draft: { ...state.draft, reworkNote: action.note } };
    }
    case "resetFromStep": {
      const { step } = action;
      // Удаляем поля шагов ≥ step. Step 1 → весь draft. Step 2 → project+.
      // Step 3 → element+hours. Step 4 → hours.
      const draft = { ...state.draft };
      if (step <= 1) {
        draft.workTypeId = null;
        draft.workTypeText = null;
      }
      if (step <= 2) {
        draft.projectId = null;
        draft.projectCodeRaw = null;
        draft.projectSkipped = false;
        // §115/2.1 — пара меняется вместе с проектом → идентификатор доработки снимаем.
        draft.reworkNote = null;
      }
      if (step <= 3) {
        draft.assemblyElementId = null;
        draft.elementSkipped = false;
      }
      // Hours всегда сбрасываются если step <= 4.
      draft.hoursHalf = null;
      return { step, draft };
    }
    case "goBack": {
      // §20/8.3: общий × вверху на шагах 2+ возвращает на предыдущий шаг
      // БЕЗ очистки draft. Это «отмена просмотра», не «отмена выбора»
      // (последнее живёт в pill-Trash2 → resetFromStep).
      if (state.step <= 1) return state;
      let prevStep = (state.step - 1) as Step;
      // Шаг «элемент» неприменим без известного проекта — при возврате из
      // часов минуем его, чтобы не встать на пустой шаг (симметрично прямому
      // переходу «проект → часы» для ручного/пропущенного проекта).
      if (prevStep === 3 && state.draft.projectId === null) {
        prevStep = 2;
      }
      return { ...state, step: prevStep };
    }
    case "prefill": {
      return { step: action.step, draft: action.draft };
    }
    default:
      return state;
  }
}
